// Calendar & analytics — vista mensual del journal con heatmap, KPIs y
// breakdowns. Click día → drawer con trades del día.

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Trade, TradingAccount } from '@bini-trading/shared-types';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Card, Stat, Section } from '../components/ui/Card';
import { MonthCalendar, type DayAggregate } from '../components/calendar/MonthCalendar';
import { EquityCurve } from '../components/calendar/EquityCurve';
import { TradeDetailModal } from '../components/trades/TradeDetailModal';
import { TradeFormModal } from '../components/trades/TradeFormModal';

interface CalendarResponse {
  month: string;
  days: DayAggregate[];
}
interface PendingSummary {
  totalPending: number;
  openPending: number;
  closedPending: number;
}
interface SymbolBreakdown {
  symbol: string;
  trades: number;
  pnl: number;
  wins: number;
}
interface OutcomeBreakdown {
  outcome: string;
  trades: number;
  pnl: number;
}
interface SummaryResponse {
  tradesTotal: number;
  tradesClosed: number;
  tradesOpen: number;
  wins: number;
  losses: number;
  breakeven: number;
  winrate: number;
  totalPnl: number;
  totalFees: number;
  avgWin: number;
  avgLoss: number;
  rr: number;
  maxDrawdown: number;
  best: { id: string; symbol: string; pnl: number; entryAt: string } | null;
  worst: { id: string; symbol: string; pnl: number; entryAt: string } | null;
  bySymbol: SymbolBreakdown[];
  byOutcome: OutcomeBreakdown[];
  bySide: {
    long: { trades: number; pnl: number; wins: number };
    short: { trades: number; pnl: number; wins: number };
  };
  equityCurve: { date: string; pnl: number; cumulative: number }[];
}

const fmtMoney = (n: number, opts: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2, ...opts }).format(n);
const fmtPct = (n: number): string => `${(n * 100).toFixed(1)}%`;

const MONTH_LABELS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const OUTCOME_LABEL: Record<string, string> = {
  tp: '✓ TP',
  sl: '✕ SL',
  manual: '✋ Manual',
  breakeven: '= BE',
  trailing: '↗ Trail',
  sin_marcar: 'Sin marcar',
};

function currentMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(monthStr: string, delta: number): string {
  const [y, m] = monthStr.split('-').map(Number);
  const date = new Date(y!, m! - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export default function Calendar(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get('accountId') ?? '';
  const urlDate = searchParams.get('date');
  const [selectedAccountId, setSelectedAccountId] = useState<string>(urlAccountId);
  const [month, setMonth] = useState<string>(() => urlDate?.slice(0, 7) ?? currentMonthStr());
  const [selectedDate, setSelectedDate] = useState<string | null>(urlDate);
  const [detailTrade, setDetailTrade] = useState<Trade | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<TradingAccount[]>('/accounts'),
  });

  const accountId =
    selectedAccountId || (accounts && accounts.length > 0 ? accounts[0]!.id : '');

  useEffect(() => {
    if (accountId && accountId !== urlAccountId) {
      const next = new URLSearchParams(searchParams);
      next.set('accountId', accountId);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const { data: calendar } = useQuery({
    queryKey: ['analytics-calendar', accountId, month],
    queryFn: () =>
      api<CalendarResponse>(
        `/analytics/calendar?month=${month}${accountId ? `&accountId=${accountId}` : ''}`,
      ),
    enabled: !!accountId,
  });

  const { data: summary } = useQuery({
    queryKey: ['analytics-summary', accountId],
    queryFn: () =>
      api<SummaryResponse>(`/analytics/summary${accountId ? `?accountId=${accountId}` : ''}`),
    enabled: !!accountId,
  });

  const { data: pendingSummary } = useQuery({
    queryKey: ['pending-summary', accountId],
    queryFn: () =>
      api<PendingSummary>(
        `/analytics/pending-summary${accountId ? `?accountId=${accountId}` : ''}`,
      ),
    enabled: !!accountId,
  });

  const { data: dayTrades } = useQuery({
    queryKey: ['day-trades', accountId, selectedDate],
    queryFn: async () => {
      const all = await api<Trade[]>(
        `/trades${accountId ? `?accountId=${accountId}` : ''}`,
      );
      return all.filter((t) => t.entryAt.slice(0, 10) === selectedDate);
    },
    enabled: !!accountId && !!selectedDate,
  });

  const accountOptions = useMemo(
    () => (accounts ?? []).map((a) => ({ value: a.id, label: `${a.name} · ${a.broker}` })),
    [accounts],
  );

  const noAccounts = !accountsLoading && (!accounts || accounts.length === 0);
  const [yearStr, monthIdx] = month.split('-');
  const monthLabel = `${MONTH_LABELS[Number(monthIdx) - 1]} ${yearStr}`;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] text-ink-subtle tracking-[0.22em] uppercase mb-2">
              Calendario · analytics
            </div>
            <h1 className="font-display text-4xl tracking-tight text-ink leading-tight capitalize">
              {monthLabel}
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              Mirá tu mes en un solo lugar — P&L diario, métricas y curva de equity.
            </p>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            {accountId && (
              <Button variant="primary" onClick={() => setCreating(true)}>
                + Nuevo trade
              </Button>
            )}
            {pendingSummary && pendingSummary.totalPending > 0 && (
              <Card tone="warn" padded={false} className="px-3 py-2 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-warn-DEFAULT font-semibold">
                  ⚠ Pendientes
                </div>
                <div className="text-sm font-bold text-warn-DEFAULT mt-0.5">
                  {pendingSummary.totalPending} sin registrar
                </div>
                <div className="text-[10px] text-ink-muted mt-0.5">
                  {pendingSummary.openPending} abiertos · {pendingSummary.closedPending} cerrados
                </div>
              </Card>
            )}
            {accounts && accounts.length > 1 && (
              <div className="w-56">
                <Select
                  label="Cuenta"
                  options={accountOptions}
                  value={accountId}
                  onChange={setSelectedAccountId}
                />
              </div>
            )}
          </div>
        </header>

        {noAccounts ? (
          <NoData message="Necesitás una cuenta + trades para ver tu journal aquí.">
            <Link to="/accounts">
              <Button variant="primary">Crear cuenta</Button>
            </Link>
          </NoData>
        ) : !summary ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Stat
                label="P&L total"
                value={fmtMoney(summary.totalPnl, { signDisplay: 'always' })}
                tone={summary.totalPnl > 0 ? 'win' : summary.totalPnl < 0 ? 'loss' : 'neutral'}
                hint={`${summary.tradesClosed} trades cerrados`}
                trend={summary.totalPnl > 0 ? 'up' : summary.totalPnl < 0 ? 'down' : null}
              />
              <Stat
                label="Winrate"
                value={summary.tradesClosed > 0 ? fmtPct(summary.winrate) : '—'}
                hint={`${summary.wins}W · ${summary.losses}L · ${summary.breakeven}BE`}
                tone="accent"
              />
              <Stat
                label="Risk / Reward"
                value={summary.rr > 0 ? `1 : ${summary.rr.toFixed(2)}` : '—'}
                hint={`avg win ${fmtMoney(summary.avgWin)} / loss ${fmtMoney(summary.avgLoss)}`}
              />
              <Stat
                label="Drawdown máx"
                value={fmtMoney(summary.maxDrawdown)}
                tone={summary.maxDrawdown > 0 ? 'loss' : 'neutral'}
                hint="peak-to-trough"
              />
            </div>

            {/* Calendar + Day panel */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMonth(shiftMonth(month, -1))}
                      className="w-9 h-9 rounded-full border border-surface-border bg-surface-elevated hover:bg-surface-panel text-ink-muted hover:text-ink transition-colors flex items-center justify-center"
                      aria-label="Mes anterior"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => setMonth(currentMonthStr())}
                      disabled={month === currentMonthStr()}
                      className="px-3 h-9 rounded-full border border-surface-border bg-surface-elevated hover:bg-surface-panel text-xs uppercase tracking-wider text-ink-muted hover:text-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Hoy
                    </button>
                    <button
                      onClick={() => setMonth(shiftMonth(month, 1))}
                      className="w-9 h-9 rounded-full border border-surface-border bg-surface-elevated hover:bg-surface-panel text-ink-muted hover:text-ink transition-colors flex items-center justify-center"
                      aria-label="Mes siguiente"
                    >
                      →
                    </button>
                  </div>
                </div>
                <MonthCalendar
                  month={month}
                  days={calendar?.days ?? []}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-4">
                  <Section
                    title={
                      selectedDate
                        ? new Date(selectedDate + 'T12:00:00Z').toLocaleDateString('es-CL', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })
                        : 'Seleccioná un día'
                    }
                  >
                    <DayTradesPanel
                      date={selectedDate}
                      trades={dayTrades}
                      onTradeClick={setDetailTrade}
                    />
                  </Section>
                </div>
              </div>
            </div>

            {/* Equity curve */}
            <Card className="mb-6">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em]">
                  Equity curve
                </h2>
                <div className="text-xs text-ink-subtle font-mono">
                  {summary.equityCurve.length} pts · acumulado{' '}
                  <span
                    className={
                      summary.totalPnl >= 0 ? 'text-win-DEFAULT' : 'text-loss-DEFAULT'
                    }
                  >
                    {fmtMoney(summary.totalPnl, { signDisplay: 'always' })}
                  </span>
                </div>
              </div>
              <EquityCurve points={summary.equityCurve} height={200} />
            </Card>

            {/* Breakdowns */}
            <div className="grid md:grid-cols-3 gap-4">
              <BreakdownPanel
                title="Por símbolo"
                rows={summary.bySymbol.map((s) => ({
                  label: s.symbol,
                  sub: `${s.trades} · ${s.trades > 0 ? fmtPct(s.wins / s.trades) : '—'} WR`,
                  value: fmtMoney(s.pnl, { signDisplay: 'always' }),
                  tone: s.pnl > 0 ? 'win' : s.pnl < 0 ? 'loss' : 'neutral',
                }))}
              />
              <BreakdownPanel
                title="Por resultado"
                rows={summary.byOutcome.map((o) => ({
                  label: OUTCOME_LABEL[o.outcome] ?? o.outcome,
                  sub: `${o.trades} trade${o.trades === 1 ? '' : 's'}`,
                  value: fmtMoney(o.pnl, { signDisplay: 'always' }),
                  tone: o.pnl > 0 ? 'win' : o.pnl < 0 ? 'loss' : 'neutral',
                }))}
              />
              <BreakdownPanel
                title="Por dirección"
                rows={[
                  {
                    label: '↑ Long',
                    sub: `${summary.bySide.long.trades} · ${
                      summary.bySide.long.trades > 0
                        ? fmtPct(summary.bySide.long.wins / summary.bySide.long.trades)
                        : '—'
                    } WR`,
                    value: fmtMoney(summary.bySide.long.pnl, { signDisplay: 'always' }),
                    tone:
                      summary.bySide.long.pnl > 0
                        ? 'win'
                        : summary.bySide.long.pnl < 0
                        ? 'loss'
                        : 'neutral',
                  },
                  {
                    label: '↓ Short',
                    sub: `${summary.bySide.short.trades} · ${
                      summary.bySide.short.trades > 0
                        ? fmtPct(summary.bySide.short.wins / summary.bySide.short.trades)
                        : '—'
                    } WR`,
                    value: fmtMoney(summary.bySide.short.pnl, { signDisplay: 'always' }),
                    tone:
                      summary.bySide.short.pnl > 0
                        ? 'win'
                        : summary.bySide.short.pnl < 0
                        ? 'loss'
                        : 'neutral',
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>

      <TradeDetailModal
        trade={detailTrade}
        accounts={accounts ?? []}
        onClose={() => setDetailTrade(null)}
      />

      {creating && accounts && (
        <TradeFormModal
          open={creating}
          onClose={() => setCreating(false)}
          accounts={accounts}
          defaultAccountId={accountId}
        />
      )}
    </AppLayout>
  );
}

function BreakdownPanel({
  title,
  rows,
}: {
  title: string;
  rows: Array<{
    label: string;
    sub: string;
    value: string;
    tone: 'win' | 'loss' | 'neutral';
  }>;
}): ReactElement {
  return (
    <Card>
      <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-[0.2em] mb-3">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-ink-faint italic">Sin datos</div>
      ) : (
        <div className="divide-y divide-surface-border">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <div className="font-mono text-sm font-semibold text-ink truncate">{r.label}</div>
                <div className="text-[10px] text-ink-subtle">{r.sub}</div>
              </div>
              <div
                className={`font-mono text-sm font-bold tabular-nums shrink-0 ml-2 ${
                  r.tone === 'win'
                    ? 'text-win-DEFAULT'
                    : r.tone === 'loss'
                    ? 'text-loss-DEFAULT'
                    : 'text-ink-muted'
                }`}
              >
                {r.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DayTradesPanel({
  date,
  trades,
  onTradeClick,
}: {
  date: string | null;
  trades: Trade[] | undefined;
  onTradeClick: (t: Trade) => void;
}): ReactElement {
  if (!date) {
    return (
      <Card padded={false} className="p-8 text-center border-dashed">
        <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-500/10 text-accent-500 flex items-center justify-center text-lg">
          ◷
        </div>
        <p className="text-xs text-ink-subtle">Click un día del calendario para ver sus trades.</p>
      </Card>
    );
  }
  if (!trades || trades.length === 0) {
    return (
      <Card padded={false} className="p-6 text-center">
        <p className="text-xs text-ink-subtle">No hay trades este día.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-2 animate-slide-in-right">
      {trades.map((t) => {
        const isLong = t.side === 'long';
        const pnl = t.pnl ?? 0;
        const isOpen = t.status === 'open';
        const isPending = t.journal?.isPending ?? false;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onTradeClick(t)}
            className={`w-full text-left rounded-xl border p-3 transition-all flex items-center gap-3 ${
              isPending
                ? 'border-warn-DEFAULT/40 bg-warn-DEFAULT/5 hover:border-warn-DEFAULT/60'
                : 'border-surface-border bg-surface-elevated hover:border-surface-strong hover:shadow-soft'
            }`}
          >
            <div
              className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-bold border ${
                isLong
                  ? 'bg-win-DEFAULT/10 border-win-DEFAULT/40 text-win-DEFAULT'
                  : 'bg-loss-DEFAULT/10 border-loss-DEFAULT/40 text-loss-DEFAULT'
              }`}
            >
              {isLong ? '↑' : '↓'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono text-sm font-bold text-ink">{t.symbol}</span>
                {isOpen && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-info-DEFAULT bg-info-DEFAULT/10 border border-info-DEFAULT/30 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-info-DEFAULT animate-pulse" />
                    Live
                  </span>
                )}
                {isPending && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-warn-DEFAULT bg-warn-DEFAULT/10 border border-warn-DEFAULT/30 px-1.5 py-0.5 rounded">
                    ⚠ Pendiente
                  </span>
                )}
              </div>
              <div className="text-[10px] text-ink-subtle font-mono">
                {new Date(t.entryAt).toLocaleTimeString('es-CL', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <div
              className={`font-mono text-sm font-bold tabular-nums ${
                isOpen
                  ? 'text-info-DEFAULT'
                  : pnl > 0
                  ? 'text-win-DEFAULT'
                  : pnl < 0
                  ? 'text-loss-DEFAULT'
                  : 'text-ink-muted'
              }`}
            >
              {isOpen ? '●' : t.pnl != null ? fmtMoney(t.pnl, { signDisplay: 'always' }) : '—'}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function NoData({
  message,
  children,
}: {
  message: string;
  children?: React.ReactNode;
}): ReactElement {
  return (
    <Card padded={false} className="p-12 text-center border-dashed">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-sheen flex items-center justify-center text-stone-950 text-2xl font-bold">
        ▦
      </div>
      <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">{message}</p>
      {children}
    </Card>
  );
}
